(ns io.daskul.tutorial.webapp.views.today
  "Today home (SMS-144) — the admin command center, the first surface of the
   Pipeline Revamp (feature 0013 §Surfaces → \"Today (home)\";
   screenshot 01-today.png).

   Replaces the generic Dashboard as the post-login home (decision: 0013 §4
   \"Home is task-based\" — Today owns the home route; the framework Dashboard's
   server-driven Overview is demoted out of the primary nav). Mounted at the
   `:home` route for admins (see app/core main-content + nav-sidebar), so the
   sidebar's home tile lands here.

   Layout (0013 §Today): a centered column with a greeting, a 4-card funnel row
   (New / In progress / Trial / Enrolled counts ← leads counts-by-state), then a
   2-col \"Needs you now\" grid. Slice 1 ships the funnel + the \"Waiting on your
   reply\" list (needs-reply conversations, capped ~5). Slices 2–3 thicken the
   grid (Trials today · Follow-ups due) and wire the \"View all →\" footers.

   Everything is branch-scoped: lists/counts carry the active-context branch
   (the header branch switcher), matching how the generic aggregate list
   resolves its hidden :branch-id filter.

   Pure number-crunching (funnel folding, greeting, capping) lives in the
   node-tested `views/today/vm` ns; this file is the JSX layout (e2e-verified)."
  (:require
   [re-frame.core :as rf]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.config.subs :as config.subs]
   [io.daskul.webapp.inbox :as inbox]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.conversation.linked-leads :as ll]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]
   [io.daskul.tutorial.webapp.domain.auth.subs :as auth.subs]
   [io.daskul.tutorial.webapp.router :as router]
   [io.daskul.tutorial.webapp.views.today.vm :as vm]))

;; ============================================================================
;; Funnel row — 4 stage-count cards (0013 §Today)
;; ============================================================================

(defui funnel-card
  "One funnel card: a tinted stage chip over a big Sora number. The whole card
   links into the Pipeline at that stage — #324. The link names the segment
   KEY, never the states behind it, so the grouping stays owned by the Lead
   aggregate definition (see vm/funnel-stages)."
  [{:keys [segment-key label color count]}]
  ($ ui/anchor
    {:href (router/href :aggregate/list {:agg "leads"} {:segment segment-key})
     :underline "never"
     :c "inherit"
     :data-testid (str "funnel-card-" label)}
    ($ ui/paper {:withBorder true :p "md" :radius "md" :shadow "xs"}
       ($ ui/stack {:gap "sm"}
          ($ ui/group {:gap "xs"}
             ($ ui/badge {:variant "light" :color color :size "md" :radius "sm"}
                label))
          ($ ui/text {:fw 700 :data-testid "funnel-count"
                      :style {:fontFamily "Sora, sans-serif" :fontSize 30 :lineHeight 1}}
             (str count))))))

(defui funnel-row
  "The funnel row. Counts, labels, and stage groupings come pre-folded from
   vm/funnel-cards, which reads them off the Lead aggregate's segment config."
  [{:keys [counts segments]}]
  ($ ui/simple-grid {:cols #js {:base 1 :xs 2 :md 4} :spacing "md"
                     :data-testid "today-funnel"}
     (for [card (vm/funnel-cards counts segments)]
       ($ funnel-card (assoc card :key (:key card) :segment-key (:key card))))))

;; ============================================================================
;; Needs-you-now — section shell + list rows
;; ============================================================================

(defui section-card
  "A \"Needs you now\" panel: a colored-dot title + header count badge, a body,
   and an optional \"View all N →\" footer link."
  [{:keys [title dot-color count view-all-href children]}]
  ($ ui/paper {:withBorder true :p "md" :radius "md" :shadow "xs"
               :style {:display "flex" :flexDirection "column"}}
     ($ ui/stack {:gap "sm" :style {:flex 1}}
        ($ ui/group {:justify "space-between" :align "center" :wrap "nowrap"}
           ($ ui/group {:gap "xs" :align "center" :wrap "nowrap"}
              ($ ui/box {:style {:width 8 :height 8 :borderRadius "50%"
                                 :background (str "var(--mantine-color-" dot-color "-6)")
                                 :flexShrink 0}})
              ($ ui/text {:fw 700 :style {:fontFamily "Sora, sans-serif"}} title))
           (when count
             ($ ui/badge {:variant "light" :color "gray" :size "sm" :radius "xl"}
                (str count))))
        children
        (when view-all-href
          ($ ui/anchor {:href view-all-href :size "sm" :c "brand" :fw 600
                        :data-testid "view-all"}
             (str "View all " count " →"))))))

(defui panel-state
  "Renders the right state for a Needs-you-now list body (0013 §States to
   implement): a skeleton while first loading, an error tile + Retry on failure,
   an empty line when there's nothing, else the children. `items` is the current
   data (used to distinguish first-load from background refresh)."
  [{:keys [loading? error items on-retry empty-text children]}]
  (cond
    error
    ($ ui/stack {:gap "xs" :py "sm" :data-testid "panel-error"}
       ($ ui/text {:size "sm" :c "dimmed"} "Couldn't load this list.")
       ($ ui/button {:size "xs" :variant "light" :color "brand"
                     :onClick on-retry} "Retry"))

    (and loading? (empty? items))
    ($ ui/centered-loader {:py "md"})

    (empty? items)
    ($ ui/text {:size "sm" :c "dimmed" :py "sm"} empty-text)

    :else children))

(defui initials-avatar
  "Small initials circle for a person row (mirrors the inbox avatar)."
  [{:keys [name]}]
  ($ ui/box {:style {:width 34 :height 34 :borderRadius "50%" :flexShrink 0
                     :background "var(--mantine-color-gray-2)"
                     :color "var(--mantine-color-gray-7)"
                     :display "flex" :alignItems "center" :justifyContent "center"
                     :fontWeight 600 :fontSize 12}}
     (inbox/initials name)))

(defui row-action
  "The quiet trailing action for a Needs-you-now row: a neutral text link with a
   chevron, replacing the per-row buttons so the task lists read as a scannable
   list rather than a wall of buttons. Kept neutral (not brand) so the row
   content leads and a column of repeated actions doesn't become a stripe of
   accent colour; interactivity comes from the chevron + hover underline."
  [{:keys [href label testid]}]
  ($ ui/anchor {:href href
                :underline "hover"
                :c "gray.7" :fw 600 :size "sm"
                :data-testid testid
                :style {:whiteSpace "nowrap" :flexShrink 0}}
     (str label " →")))

(defui waiting-row
  "One \"Waiting on your reply\" row: avatar + name/preview + a Reply link to
   the conversation thread."
  [{:keys [conversation now]}]
  (let [{:keys [id sender-name last-message-at
                last-message-preview last-message-direction]} conversation]
    ($ ui/group {:justify "space-between" :align "center" :wrap "nowrap"
                 :data-testid "waiting-row"}
       ($ ui/group {:gap "sm" :align "center" :wrap "nowrap" :style {:minWidth 0}}
          ($ initials-avatar {:name sender-name})
          ($ ui/stack {:gap 0 :style {:minWidth 0}}
             ($ ui/text {:size "sm" :fw 600 :truncate true}
                (or sender-name "Unknown"))
             ($ ui/text {:size "xs" :c "dimmed" :truncate true}
                (str (or (inbox/preview-text last-message-preview last-message-direction)
                         "New message")
                     (when-let [t (inbox/relative-time last-message-at now)]
                       (str " · " t))))))
       ($ row-action {:href (router/href :conversation/thread {:id id})
                      :label "Reply"}))))

(defui waiting-on-reply
  "Waiting on your reply panel — needs-reply conversations (capped ~5)."
  [{:keys [conversations loading? error now on-retry]}]
  (let [{:keys [rows total overflow?]} (vm/cap-list conversations 5)]
    ($ section-card
       {:title "Waiting on your reply"
        :dot-color "yellow"
        :count total
        :view-all-href (when overflow?
                         (router/href :inbox nil {:needs-reply "true"}))}
       ($ panel-state
          {:loading? loading? :error error :items conversations :on-retry on-retry
           :empty-text "You're all caught up — no replies waiting."}
          ($ ui/stack {:gap "sm"}
             (for [c rows]
               ($ waiting-row {:key (:id c) :conversation c :now now})))))))

;; ============================================================================
;; Trials today — check-in list (0013 §Today)
;; ============================================================================

(defui trial-row
  "One \"Trials today\" row: avatar + child name / trial time + a Check-in link
   to the attendee's attendance surface."
  [{:keys [sa tz]}]
  (let [{:keys [id name time]} (vm/trial-row sa tz)]
    ($ ui/group {:justify "space-between" :align "center" :wrap "nowrap"
                 :data-testid "trial-row"}
       ($ ui/group {:gap "sm" :align "center" :wrap "nowrap" :style {:minWidth 0}}
          ($ initials-avatar {:name name})
          ($ ui/stack {:gap 0 :style {:minWidth 0}}
             ($ ui/text {:size "sm" :fw 600 :truncate true} name)
             (when time
               ($ ui/text {:size "xs" :c "dimmed" :truncate true} time))))
       ($ row-action {:href (router/href :aggregate/detail
                                         {:agg "session-attendees" :id id})
                      :label "Check in"}))))

(defui trials-today
  "Trials today panel — today's trial SessionAttendees (capped ~5)."
  [{:keys [attendees loading? error tz on-retry]}]
  (let [{:keys [rows total]} (vm/cap-list attendees 5)]
    ($ section-card
       {:title "Trials today"
        :dot-color "violet"
        :count total
        ;; View-all → the dedicated Today's-trials attendance surface lands in a
        ;; separate story; until then the footer is omitted (Slice 3 note).
        :view-all-href nil}
       ($ panel-state
          {:loading? loading? :error error :items attendees :on-retry on-retry
           :empty-text "No trials scheduled for today."}
          ($ ui/stack {:gap "sm"}
             (for [sa rows]
               ($ trial-row {:key (:id sa) :sa sa :tz tz})))))))

;; ============================================================================
;; Follow-ups due (0013 §Today)
;; ============================================================================

(defui follow-up-row
  "One \"Follow-ups due\" row: avatar + lead label / program + a View link to
   the lead's detail page (where Nudge / Re-contact / Re-open live)."
  [{:keys [lead]}]
  ($ ui/group {:justify "space-between" :align "center" :wrap "nowrap"
               :data-testid "follow-up-row"}
     ($ ui/group {:gap "sm" :align "center" :wrap "nowrap" :style {:minWidth 0}}
        ($ initials-avatar {:name (ll/primary-label lead)})
        ($ ui/stack {:gap 0 :style {:minWidth 0}}
           ($ ui/text {:size "sm" :fw 600 :truncate true}
              (ll/primary-label lead))
           (when-let [sub (or (ll/program-name lead) (:follow-up-date lead))]
             ($ ui/text {:size "xs" :c "dimmed" :truncate true} sub))))
     ($ row-action {:href (router/href :aggregate/detail {:agg "leads" :id (:id lead)})
                    :label "View"})))

(defui follow-ups-due
  "Follow-ups due panel — leads whose follow-up-date is due/overdue (capped 6)."
  [{:keys [leads loading? error on-retry]}]
  (let [{:keys [rows total overflow?]} (vm/cap-list leads 6)]
    ($ section-card
       {:title "Follow-ups due"
        :dot-color "yellow"
        :count total
        :view-all-href (when overflow?
                         (router/href :aggregate/list {:agg "leads"}
                                      {:needs-follow-up "true"}))}
       ($ panel-state
          {:loading? loading? :error error :items leads :on-retry on-retry
           :empty-text "No follow-ups due today."}
          ($ ui/stack {:gap "sm"}
             (for [lead rows]
               ($ follow-up-row {:key (:id lead) :lead lead})))))))

;; ============================================================================
;; Unlinked guardians (#859) — orphaned-guardian data-quality nudge
;; ============================================================================

(defui orphan-guardians-card
  "Card for guardians with no student link (orphaned, #859). A data-quality
   nudge that keeps the orphan count near zero — the unblocker for retiring the
   Guardians listing (#840). Hidden entirely when the count is 0 (nothing to
   action), matching the #834 banner's quiet-when-empty philosophy. Review
   deep-links to the guardian list filtered to the unlinked set, where each row
   opens the guardian (link a student via the Students tab, or remove)."
  [{:keys [count]}]
  (when-let [message (vm/orphan-card-message count)]
    ($ section-card
       {:title "Unlinked guardians"
        :dot-color "orange"
        :count count}
       ($ ui/stack {:gap "sm" :align "flex-start"}
          ;; --text-muted (7:1) rather than `dimmed` (~3.5:1) — see #865.
          ($ ui/text {:size "sm" :style {:color "var(--text-muted)"}} message)
          ($ row-action {:href (router/href :aggregate/list {:agg "guardians"}
                                            {:link-status "unlinked"})
                         :label "Review"
                         :testid "orphan-guardians-review"})))))

;; ============================================================================
;; Page
;; ============================================================================

(defui today-page []
  (let [user (urf/use-subscribe [::auth.subs/current-user])
        branch-id (urf/use-subscribe [::ac.subs/branch-id])
        tz (urf/use-subscribe [::ac.subs/school-timezone])

        counts (urf/use-subscribe [::agg.subs/counts :lead :state])
        ;; The funnel's stage labels + groupings come from the Lead aggregate's
        ;; own segment config, so Today and the Pipeline can't disagree (#324).
        lead-config (urf/use-subscribe [::config.subs/aggregate :lead])

        conversations (urf/use-subscribe [::agg.subs/all :conversation])
        conv-loading? (urf/use-subscribe [::agg.subs/loading? :conversation])
        conv-error (urf/use-subscribe [::agg.subs/error :conversation])

        attendees (urf/use-subscribe [::agg.subs/all :session-attendee])
        att-loading? (urf/use-subscribe [::agg.subs/loading? :session-attendee])
        att-error (urf/use-subscribe [::agg.subs/error :session-attendee])

        follow-ups (urf/use-subscribe [::agg.subs/all :lead])
        fu-loading? (urf/use-subscribe [::agg.subs/loading? :lead])
        fu-error (urf/use-subscribe [::agg.subs/error :lead])

        ;; Unlinked-guardian count (#859) — the orphaned set, school-scoped by the
        ;; guardian counts endpoint. The card hides itself when this is 0.
        orphan-counts (urf/use-subscribe [::agg.subs/counts :guardian :link-status])
        unlinked-count (or (:unlinked orphan-counts) 0)

        ;; Read the wall clock once per render for the relative-time labels and
        ;; the greeting; the pure vm/inbox helpers stay clock-free.
        now (js/Date.)

        ;; Branch-scope every fetch with the active-context branch (matches the
        ;; generic aggregate list's hidden :branch-id filter). nil branch = all
        ;; branches the role can see (scope-fn still applies). Memoized on
        ;; branch-id so the fetch callbacks below have a stable dependency.
        branch-params (uix/use-memo
                       (fn [] (cond-> {} branch-id (assoc :branch-id branch-id)))
                       [branch-id])

        ;; Per-list fetch callbacks — reused on mount and as each panel's Retry
        ;; (0013 §States to implement: error → Retry).
        fetch-waiting! (uix/use-callback
                        #(rf/dispatch [::agg.events/list :conversation
                                       (assoc branch-params :needs-reply "true")])
                        [branch-params])
        ;; SAs don't accept a branch-id param (branch lives on the Session). The
        ;; :trials-today filter resolves today + the active branch server-side
        ;; from the request, so the client sends only :trials-today. Re-pull on
        ;; branch change so a branch switch re-scopes (keyed via branch-id).
        fetch-trials! (uix/use-callback
                       (fn [] (rf/dispatch [::agg.events/list :session-attendee
                                            {:trials-today "true"}]))
                       [branch-id])
        fetch-follow-ups! (uix/use-callback
                           #(rf/dispatch [::agg.events/list :lead
                                          (assoc branch-params :needs-follow-up "true")])
                           [branch-params])]

    ;; Fetch the funnel counts + the three Needs-you-now lists on mount / branch
    ;; change. Re-pull when the active branch switches so the surface re-scopes.
    (uix/use-effect
     (fn []
       (rf/dispatch [::agg.events/counts :lead :state branch-params])
       (rf/dispatch [::agg.events/counts :guardian :link-status branch-params])
       (fetch-waiting!)
       (fetch-trials!)
       (fetch-follow-ups!)
       js/undefined)
     [branch-params fetch-waiting! fetch-trials! fetch-follow-ups!])

    ($ ui/stack {:gap "lg" :style {:maxWidth 1180 :margin "0 auto" :width "100%"}}
       ;; Greeting
       ($ ui/stack {:gap 2}
          ($ ui/title {:order 2 :style {:fontFamily "Sora, sans-serif"}}
             (vm/greeting (:first-name user) (.getHours now)))
          ($ ui/text {:c "dimmed" :size "sm"}
             "Here's where your pipeline stands and what needs you right now."))

       ;; Funnel row
       ($ funnel-row {:counts counts :segments (vm/state-segments lead-config)})

       ;; Needs you now — 2-col grid (Waiting · Trials), Follow-ups full-width.
       ($ ui/stack {:gap "lg"}
          ;; Orphaned-guardian nudge (#859) — a small half-width card in its own
          ;; row (not a full-width banner), hidden entirely when the count is 0.
          (when (pos? unlinked-count)
            ($ ui/simple-grid {:cols #js {:base 1 :md 2} :spacing "lg"}
               ($ orphan-guardians-card {:count unlinked-count})))
          ($ ui/simple-grid {:cols #js {:base 1 :md 2} :spacing "lg"}
             ($ waiting-on-reply {:conversations conversations
                                  :loading? conv-loading?
                                  :error conv-error
                                  :on-retry fetch-waiting!
                                  :now now})
             ($ trials-today {:attendees attendees
                              :loading? att-loading?
                              :error att-error
                              :on-retry fetch-trials!
                              :tz tz}))
          ($ follow-ups-due {:leads follow-ups
                             :loading? fu-loading?
                             :error fu-error
                             :on-retry fetch-follow-ups!})))))
